import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Github, ExternalLink } from "lucide-react";

export default function JavaScriptSDK() {
  const [tab, setTab] = useState("sdk");

  return (
    <div>
      <section className="bg-[#f4f5f7] py-14 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="text-4xl font-semibold text-slate-800">JavaScript</h1>
        </div>
      </section>

      <article className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
        <Link
          to="/development-kits"
          className="inline-flex items-center gap-1 text-sm text-[#29abe2] hover:underline mb-8"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Development Kits
        </Link>

        <div className="border-b border-slate-200 flex gap-1 flex-wrap">
          {[
            { id: "sdk", label: "SDK" },
            { id: "overview", label: "Overview" },
            { id: "docs", label: "Documentation" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-6 py-3 text-sm font-semibold uppercase tracking-wide transition-colors ${
                tab === t.id
                  ? "text-[#29abe2] border-b-2 border-[#29abe2] -mb-px"
                  : "text-slate-600 hover:text-slate-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="py-10">
          {tab === "sdk" && <SdkTab />}
          {tab === "overview" && <OverviewTab />}
          {tab === "docs" && <DocsTab />}
        </div>
      </article>
    </div>
  );
}

function GitHubBadge({ href }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold px-5 py-2.5 rounded transition-colors"
    >
      <Github className="w-4 h-4" />
      Get source on GitHub
    </a>
  );
}

function SdkTab() {
  return (
    <div className="space-y-10">
      <section>
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">TCMP Tappy SDK</h2>
        <p className="text-slate-700 leading-relaxed text-[16px]">
          The TCMP Tappy SDK is highly modular and designed to work in both Chrome packaged app environments as
          well as in NodeJS utilities. If you wish to use most fundamental version of the SDK, it is best to start
          from documentation for the core TCMP Tappy driver on GitHub.
        </p>
        <div className="mt-5">
          <GitHubBadge href="https://github.com/TapTrack/TappyTcmpJs" />
        </div>

        <div className="mt-10 space-y-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 uppercase tracking-wide mb-2">
              Abstraction Wrapper
            </h3>
            <p className="text-slate-700 leading-relaxed">
              In order to limit unnecessary dependencies, the core Tappy TCMP library only provides the basic
              fundamentals of communicating with a TCMP Tappy. For most applications, it would be best to start
              from the abstraction wrapper library. This library has all the capabilities of the core SDK, but
              simplifies performing common operations by adding several convenience functions.
            </p>
            <div className="mt-4">
              <GitHubBadge href="https://github.com/TapTrack/TcmpTappyWrapperJs" />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-slate-800 uppercase tracking-wide mb-2">
              Cordova / PhoneGap
            </h3>
            <p className="text-slate-700 leading-relaxed">
              The JavaScript SDK can also be used for developing cross-platform mobile applications using
              Cordova/PhoneGap. In order to do this, you will have to develop a simple TappyCommunicator similar
              to the{" "}
              <a
                href="https://github.com/TapTrack/TappyNodeSerialCommunicatorJs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#29abe2] hover:underline"
              >
                TappyNodeSerialCommunicator
              </a>{" "}
              that wraps a USB serial port (for TappyUSBs) or Bluetooth Low Energy (for TappyBLEs) library of
              your choice. Currently, there are no officially supported TapTrack-authored communicators for
              PhoneGap/Cordova, but one of our customers has created an open-source{" "}
              <a
                href="https://github.com/debugerr/TappyBleBridge"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#29abe2] hover:underline"
              >
                BLE communicator
              </a>{" "}
              and we would be happy to help any custom integrations if you choose to pursue this route.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">Tappy Classic SDK</h2>
        <p className="text-slate-700 leading-relaxed text-[16px]">
          As the Tappy Classic protocol has been deprecated, the Classic SDK is deprecated as well. This SDK only
          supports usage in Chrome packaged apps.
        </p>
        <div className="mt-5">
          <GitHubBadge href="https://github.com/TapTrack/TappyClassicJS" />
        </div>
      </section>
    </div>
  );
}

function OverviewTab() {
  return (
    <div className="space-y-10">
      <section>
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">Overview</h2>
        <p className="text-slate-700 leading-relaxed text-[16px]">
          There are two JavaScript SDKs for working with Tappies. The main SDK is the TCMP Tappy SDK intended for
          working with current TappyUSB and TappyBLE devices either in Chrome packaged apps or in NodeJS
          applications. The second {"\u201c"}Tappy Classic{"\u201d"} SDK only works with Chrome packaged apps and is
          specifically designed for use with older TappyUSB devices that use a protocol that predates TCMP. If you
          are unsure which version of Tappy you have, all TappyBLEs and any TappyUSB devices purchased after July
          2015 are of the TCMP variety.
        </p>
      </section>

      <section>
        <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">Demos</h2>
        <p className="text-slate-700 leading-relaxed text-[16px]">
          There is a step-by-step tutorial on using the TCMP SDK to develop a command-line utility in NodeJS{" "}
          <a
            href="https://taptrack.com/whitepaper-tappyusb-node/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#29abe2] hover:underline"
          >
            here
          </a>
          .
        </p>
      </section>
    </div>
  );
}

function DocsTab() {
  return (
    <div>
      <h2 className="text-2xl md:text-3xl font-semibold text-slate-800 mb-4">Documentation</h2>
      <a
        href="https://docs.google.com/document/d/e/2PACX-1vTnzSyisbi6jD9mepVJsbgf6JL_qWjiIjGGIthRpULt2C5kkpuchm0nYa0U5NSgFOcpzzYzf6S9mGS6/pub?embedded=true"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-[#29abe2] hover:underline text-lg font-semibold"
      >
        Read the full documentation here.
        <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );
}
